import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { submitProApplication, getMyProApplication } from '../api/proApplications';
import Button from '../components/common/Button';
import Spinner from '../components/common/Spinner';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending:  { label: '審査中', color: 'bg-yellow-100 text-yellow-800' },
  approved: { label: '承認済み', color: 'bg-green-100 text-green-800' },
  rejected: { label: '不承認', color: 'bg-red-100 text-red-800' },
};

export default function ProApplicationPage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState('');
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: existing, isLoading: appLoading } = useQuery({
    queryKey: ['pro-application', 'me'],
    queryFn: getMyProApplication,
    enabled: !!user,
  });

  if (authLoading || appLoading) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  if (!user) {
    return (
      <div className="text-center py-20 space-y-3">
        <p className="text-gray-600">申請にはログインが必要です</p>
      </div>
    );
  }

  if (user.is_pro) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-3">
        <div className="text-4xl">✅</div>
        <h1 className="text-2xl font-bold">公認雀士です</h1>
        <p className="text-gray-500">あなたはすでに公認雀士として登録されています。</p>
        <Button onClick={() => navigate('/profile')} variant="secondary">マイページへ</Button>
      </div>
    );
  }

  const statusInfo = existing ? STATUS_LABELS[existing.status] : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await submitProApplication({
        credentials: credentials.trim(),
        evidenceFile,
      });
      setSubmitted(true);
    } catch (err: any) {
      if (err.message?.includes('already pending')) {
        setError('すでに申請中の審査があります。');
      } else {
        setError(err.message ?? '送信に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4 text-center space-y-4">
        <div className="text-5xl">🎉</div>
        <h1 className="text-2xl font-bold">申請を受け付けました</h1>
        <p className="text-gray-500">
          審査結果はプロフィールページでご確認いただけます。<br />
          通常1〜3営業日以内にご連絡します。
        </p>
        <Button onClick={() => navigate('/profile')} variant="secondary">マイページへ</Button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-8 px-4 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">公認雀士申請</h1>
        <p className="text-sm text-gray-500">
          審査通過後、プロフィールに「公認雀士」バッジが表示されます。
        </p>
      </div>

      {/* 既存の申請状況 */}
      {existing && statusInfo && (
        <div className={`border rounded-xl p-4 flex items-center justify-between ${
          existing.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
          existing.status === 'approved' ? 'bg-green-50 border-green-200' :
          'bg-red-50 border-red-200'
        }`}>
          <div>
            <p className="text-sm font-medium text-gray-700">直近の申請状況</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {new Date(existing.created_at).toLocaleDateString('ja-JP')} 申請
            </p>
          </div>
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${statusInfo.color}`}>
            {statusInfo.label}
          </span>
        </div>
      )}

      {/* 申請条件 */}
      <div className="border rounded-xl p-4 bg-blue-50 border-blue-200 space-y-2">
        <p className="text-sm font-medium text-blue-800">公認雀士の条件（いずれか）</p>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>麻雀プロ資格保持者</li>
          <li>段位戦での段位・タイトル実績がある方</li>
          <li>管理者の判断で認定する場合があります</li>
        </ul>
      </div>

      {/* 申請フォーム（rejected の場合は再申請可） */}
      {(!existing || existing.status === 'rejected') && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              資格・実績 <span className="text-red-500">*</span>
            </label>
            <textarea
              className="w-full border rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
              rows={4}
              placeholder="例：天鳳八段、最高位戦プロ資格保持、〇〇大会優勝など"
              maxLength={2000}
              value={credentials}
              onChange={(e) => setCredentials(e.target.value)}
              required
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{credentials.length}/2000</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              証明資料を添付（任意）
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              onChange={(e) => setEvidenceFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
            />
            <p className="text-xs text-gray-400 mt-1">
              段位証明・プロ資格証・大会結果など。JPEG / PNG / PDF・10MB以内
            </p>
            {evidenceFile && (
              <p className="text-xs text-green-600 mt-1">✓ {evidenceFile.name}</p>
            )}
          </div>

{error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" loading={isSubmitting} disabled={!credentials.trim()}>
              申請する
            </Button>
          </div>
        </form>
      )}

      {existing?.status === 'pending' && (
        <p className="text-sm text-gray-500 text-center py-4">
          審査中のため新しい申請はできません。結果をお待ちください。
        </p>
      )}
    </div>
  );
}
